// 📊 StatsSection - Section des statistiques clés du festival
// Affiche les chiffres importants (artistes, spectateurs, date, lieu)
// Utilisé dans : src/pages/HomePage.jsx

import { Users, Mic2, Calendar, MapPin } from 'lucide-react'
import homeData from '@/locales/home.json'
import eventData from '@/locales/event.json'
import globalData from '@/locales/global.json'

/**
 * Composant de présentation des statistiques du festival
 * Affiche 4 métriques principales dans une grille responsive
 * Inclut une description textuelle de l'expérience Rock'In Agde
 */
export default function StatsSection() {
  const { stats } = homeData
  const { nextEdition } = eventData
  const { labels } = globalData

  return (
    <section className="section-padding bg-white dark:bg-neutral-900">
      <div className="container-custom">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {/* Artistes */}
          <div className="text-center p-6 bg-gradient-to-br from-primary-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl border border-primary-100 dark:border-neutral-700 hover:shadow-xl transition-all hover:scale-105">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-950 rounded-full mb-4">
              <Users className="text-primary-600 dark:text-primary-400" size={32} />
            </div>
            <div className="text-4xl md:text-5xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              {stats.artists}
            </div>
            <div className="text-neutral-600 dark:text-neutral-400 font-medium">
              {labels.artistes}
            </div>
          </div>

          {/* Spectateurs */}
          <div className="text-center p-6 bg-gradient-to-br from-primary-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl border border-primary-100 dark:border-neutral-700 hover:shadow-xl transition-all hover:scale-105">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-950 rounded-full mb-4">
              <Mic2 className="text-primary-600 dark:text-primary-400" size={32} />
            </div>
            <div className="text-4xl md:text-5xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              {stats.spectators}
            </div>
            <div className="text-neutral-600 dark:text-neutral-400 font-medium">
              {labels.spectateurs}
            </div>
          </div>

          {/* Date */}
          <div className="text-center p-6 bg-gradient-to-br from-primary-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl border border-primary-100 dark:border-neutral-700 hover:shadow-xl transition-all hover:scale-105">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-950 rounded-full mb-4">
              <Calendar className="text-primary-600 dark:text-primary-400" size={32} />
            </div>
            <div className="text-2xl md:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              {nextEdition.date}
            </div>
            <div className="text-neutral-600 dark:text-neutral-400 font-medium">
              {labels.prochaineEdition}
            </div>
          </div>

          {/* Lieu */}
          <div className="text-center p-6 bg-gradient-to-br from-primary-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl border border-primary-100 dark:border-neutral-700 hover:shadow-xl transition-all hover:scale-105">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-950 rounded-full mb-4">
              <MapPin className="text-primary-600 dark:text-primary-400" size={32} />
            </div>
            <div className="text-xl md:text-2xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              {nextEdition.location}
            </div>
            <div className="text-neutral-600 dark:text-neutral-400 font-medium text-sm">
              {labels.lieu}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mt-16 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-display text-primary-600 dark:text-primary-400 mb-6">
            UNE EXPÉRIENCE UNIQUE
          </h2>
          <div className="space-y-4 text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed">
            <p>Chaque année, le coeur d'Agde bat au rythme du Rock'In Agde : un concert unique en France où plus de 100 artistes, professionnels et talent cachés, partagent la même scène dans une ambiance explosive.</p>
            <p>Entre énergie pure, passion musicale et rencontres humaines, le Rock'In Agde c'est un show grandiose à ciel ouvert, au coeur du centre historique d'Agde.</p>
            <p>Prêt à vivre une expérience sonore et visuelle inoubliable ?</p>
          </div>
        </div>
      </div>
    </section>
  )
}
